import { CalendarDays, FileDown, Mail, MessageSquareText } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import type { ScreenerAsset } from "../../types";
import { AnimatedExpandedDetails } from "./AnimatedExpandedDetails";
import { AssetResultRowCharts } from "./AssetResultRowCharts";
import {
    ComingSoonCtaButton,
    ctaButtonClassName,
    secondaryCtaButtonClassName,
} from "./ComingSoonCtaButton";
import { ExpandedDetailField } from "./ExpandedDetailField";
import {
    formatAUM,
    formatHoldersCount,
    formatMinInvestment,
    formatFee,
    formatList,
    formatNetworks,
    formatLiquidity,
} from "./functions";
import { useAssetChat } from "~/modules/asset_chat/hooks/useAssetChat";
import { getCorpusAsset, getCorpusAssetId } from "~/modules/asset_chat/assets";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";
import { getCalApi } from "@calcom/embed-react";
import * as Analytics from "~/modules/asset_screener/analytics";

// // // //

function isJaaaAsset(asset: ScreenerAsset): boolean {
    return asset.symbol.toUpperCase() === "JAAA";
}

/** Soft blue treatment for the primary CTA — matches the data room chip. */
const primaryCtaButtonClassName = cn(
    ctaButtonClassName,
    "border-blue-200 bg-primary-foreground text-blue-700",
    "hover:border-blue-300 hover:bg-primary-light hover:text-blue-800",
);

// // // //

export function AssetResultRowDetails(props: {
    asset: ScreenerAsset;
    isExpanded: boolean;
    isLoggedIn: boolean;
}) {
    const { asset, isExpanded, isLoggedIn } = props;
    const { openNewChat } = useAssetChat();

    // Non-null only for assets that have a document corpus (keyed by symbol; see
    // asset_chat/assets.ts).
    const corpusAssetId = getCorpusAssetId(asset);
    const corpusAsset = corpusAssetId ? getCorpusAsset(corpusAssetId) : null;

    const longDescriptionSections = corpusAsset
        ? [
              {
                  title: "Target Return",
                  text: corpusAsset.longDescription.return,
              },
              {
                  title: "Liquidity",
                  text: corpusAsset.longDescription.liquidity,
              },
              {
                  title: "Management",
                  text: corpusAsset.longDescription.management,
              },
          ]
        : [];

    const rowMetrics = {
        aum: formatAUM(asset.stats.aum),
        investors: formatHoldersCount(asset.stats.holdersCount),
        redemption: formatLiquidity(asset.redemptionFrequency.label),
        minInvestment: formatMinInvestment(asset.minInvestment),
        networks: formatNetworks(asset.networks),
        eligibility: formatList(asset.stats.investorTypes),
        fee: formatFee(asset.stats.fee),
        performanceFee: formatFee(asset.stats.performanceFee),
        subscriptionFee: formatFee(asset.stats.subscriptionFee),
        redemptionFee: formatFee(asset.stats.redemptionFee),
    };

    const openDealRoomChat = () => {
        if (!corpusAssetId) {
            return;
        }
        openNewChat({
            assetCorpusId: corpusAssetId,
            assetName: asset.symbol,
        });
    };

    const bookMeetingConfig = corpusAsset?.meeting ?? null;

    const openBookMeetingModal = async () => {
        if (!bookMeetingConfig) {
            return;
        }

        Analytics.trackAssetCardClickBookMeeting({
            asset_id: asset.id,
            asset_ticker: asset.symbol,
            manager_name: bookMeetingConfig.managerName,
            cal_link: bookMeetingConfig.calLink,
        });

        const cal = await getCalApi({ namespace: "screener-book-meeting" });
        cal("ui", { layout: "month_view" });
        cal("modal", { calLink: bookMeetingConfig.calLink });
    };

    const showJaaaProductSummary = isJaaaAsset(asset);
    const incomeTreatment = showJaaaProductSummary
        ? "Accumulates daily"
        : asset.incomeTreatment;

    return (
        <AnimatedExpandedDetails isExpanded={isExpanded}>
            <div className="border-t px-6 py-6 xl:px-10">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-x-10">
                    <div className="flex min-w-0 flex-col gap-6">
                        {isExpanded && <AssetResultRowCharts asset={asset} />}

                        <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                                Fees
                            </p>
                            <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3">
                                <ExpandedDetailField
                                    label="Management"
                                    value={rowMetrics.fee}
                                />
                                <ExpandedDetailField
                                    label="Performance"
                                    value={rowMetrics.performanceFee}
                                />
                                <ExpandedDetailField
                                    label="Other"
                                    value={asset.stats.otherFees ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Subscription"
                                    value={rowMetrics.subscriptionFee}
                                />
                                <ExpandedDetailField
                                    label="Redemption"
                                    value={rowMetrics.redemptionFee}
                                />
                            </div>
                        </div>

                        <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                                Service Providers
                            </p>
                            <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3">
                                <ExpandedDetailField
                                    label="Manager"
                                    value={asset.manager?.name ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Fund administrator"
                                    value={
                                        asset.serviceProviders.administrator ??
                                        "—"
                                    }
                                />
                                <ExpandedDetailField
                                    label="Custodian"
                                    value={
                                        asset.serviceProviders.custodian ?? "—"
                                    }
                                />
                                <ExpandedDetailField
                                    label="Auditor"
                                    value={
                                        asset.serviceProviders.auditor ?? "—"
                                    }
                                />
                                <ExpandedDetailField
                                    label="Transfer agent"
                                    value={
                                        asset.serviceProviders.transferAgent ??
                                        "—"
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6">
                        {(longDescriptionSections.length > 0 ||
                            corpusAssetId) && (
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">
                                    Product Summary
                                </p>
                                <div className="mt-3 rounded-md border bg-accent p-5">
                                    {longDescriptionSections.length > 0 && (
                                        <div className="grid gap-3">
                                            {longDescriptionSections.map(
                                                (section) => (
                                                    <p
                                                        key={section.title}
                                                        className="text-sm leading-6 text-secondary-foreground"
                                                    >
                                                        <span className="font-medium text-foreground">
                                                            {section.title}.
                                                        </span>{" "}
                                                        {section.text}
                                                    </p>
                                                ),
                                            )}
                                        </div>
                                    )}

                                    {/* CTAs are tied to data-room availability: a manager
                            sharing their data room implies a relationship where
                            we actively drive traffic to them. No data room, no
                            CTA row. */}
                                    {corpusAssetId && (
                                        <div className="mt-4 grid min-w-0 grid-cols-2 gap-2">
                                            <SelectLoginGate
                                                isLoggedIn={isLoggedIn}
                                                tooltipText="Log in to start a data room chat"
                                            >
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className={
                                                        primaryCtaButtonClassName
                                                    }
                                                    disabled={!isLoggedIn}
                                                    onClick={openDealRoomChat}
                                                >
                                                    <MessageSquareText aria-hidden="true" />
                                                    <span className="bg-[linear-gradient(100deg,var(--blue-700)_40%,var(--blue-400)_50%,var(--blue-700)_60%)] bg-[length:200%_100%] bg-clip-text text-transparent motion-safe:animate-cta-text-shimmer">
                                                        Start AI Diligence Chat
                                                    </span>
                                                </Button>
                                            </SelectLoginGate>
                                            {bookMeetingConfig ? (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className={cn(
                                                        secondaryCtaButtonClassName,
                                                        "cursor-pointer",
                                                    )}
                                                    onClick={
                                                        openBookMeetingModal
                                                    }
                                                    aria-label={`Book a meeting with ${bookMeetingConfig.managerName}`}
                                                >
                                                    <CalendarDays aria-hidden="true" />
                                                    Book Meeting
                                                </Button>
                                            ) : (
                                                <ComingSoonCtaButton
                                                    icon={
                                                        <CalendarDays aria-hidden="true" />
                                                    }
                                                    label="Book Meeting"
                                                />
                                            )}
                                            <ComingSoonCtaButton
                                                icon={
                                                    <FileDown aria-hidden="true" />
                                                }
                                                label="Download Fact Sheet"
                                            />
                                            <ComingSoonCtaButton
                                                icon={
                                                    <Mail aria-hidden="true" />
                                                }
                                                label="Contact Manager"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                                Key Terms
                            </p>
                            <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
                                <ExpandedDetailField
                                    label="Minimum investment"
                                    value={rowMetrics.minInvestment}
                                />
                                <ExpandedDetailField
                                    label="Eligible investors"
                                    value={rowMetrics.eligibility}
                                />
                                <ExpandedDetailField
                                    label="Fund structure"
                                    value={asset.fundStructure ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Income"
                                    value={incomeTreatment ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Subscriptions"
                                    value={asset.subscriptionFrequency ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Redemptions"
                                    value={rowMetrics.redemption}
                                />
                                <ExpandedDetailField
                                    label="Inception date"
                                    value={asset.stats.inception}
                                />
                                <ExpandedDetailField
                                    label="Platforms"
                                    value={asset.platform?.name ?? "—"}
                                />
                                <ExpandedDetailField
                                    label="Networks"
                                    value={rowMetrics.networks}
                                />
                            </div>
                        </div>
                    </div>

                    {/* <div className="flex min-w-0 flex-col gap-5">
                            {productSummarySections.length > 0 && (
                                <div className="rounded-md border border-primary-light bg-primary-foreground/45 p-4">
                                    <div className="grid gap-4">
                                        {productSummarySections.map(
                                            (section) => (
                                                <div key={section.title}>
                                                    <p className="text-sm font-medium text-blue-950">
                                                        {section.title}
                                                    </p>
                                                    <p className="mt-2 text-sm leading-6 text-secondary-foreground">
                                                        {section.text}
                                                    </p>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="min-w-0 w-full">
                                <ManagersPerspectiveCallout
                                    assetId={asset.id}
                                    assetTicker={asset.symbol}
                                    assetName={asset.name}
                                    assetCategory={asset.assetClass.name}
                                    managerName={asset.manager?.name ?? null}
                                />
                            </div>

                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    Documents
                                </p>
                                <div className="mt-3 flex flex-wrap items-center justify-start gap-1.5">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className={
                                            compactAssetDescriptionActionButtonClassName
                                        }
                                        disabled={!asset.hasDataRoom}
                                        onClick={() => {
                                            if (!asset.hasDataRoom) {
                                                return;
                                            }

                                            track(
                                                AssetScreenerUserEvents.AssetCardClickAccessDataRoom,
                                                {
                                                    asset_id: String(asset.id),
                                                    asset_ticker: asset.symbol,
                                                    document_count:
                                                        asset.dataRoomDocuments
                                                            .length,
                                                },
                                            );
                                            setDataRoomAccessMode("data-room");
                                            setShowDataRoomModal(true);
                                        }}
                                    >
                                        <FolderLock
                                            aria-hidden="true"
                                            className="opacity-80"
                                        />
                                        Access Data Room
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className={
                                            compactAssetDescriptionActionButtonClassName
                                        }
                                        disabled={!factsheetDocument}
                                        onClick={() => {
                                            if (!factsheetDocument) {
                                                return;
                                            }

                                            track(
                                                AssetScreenerUserEvents.AssetCardClickAccessDataRoom,
                                                {
                                                    asset_id: String(asset.id),
                                                    asset_ticker: asset.symbol,
                                                    document_count: 1,
                                                    access_type: "factsheet",
                                                },
                                            );
                                            setDataRoomAccessMode("factsheet");
                                            setShowDataRoomModal(true);
                                        }}
                                    >
                                        <FileText
                                            aria-hidden="true"
                                            className="opacity-80"
                                        />
                                        Access 1-Pager Factsheet
                                    </Button>

                                    <Button
                                        asChild
                                        variant="outline"
                                        size="sm"
                                        className={
                                            compactAssetDescriptionActionButtonClassName
                                        }
                                    >
                                        <Link
                                            href={analyticsHref}
                                            aria-label={`View full market data for ${asset.name}`}
                                            analyticsLabel={
                                                AssetScreenerUserEvents.AssetCardClickViewAnalytics
                                            }
                                            analyticsEventProperties={{
                                                asset_ticker: asset.symbol,
                                            }}
                                        >
                                            <LineChart aria-hidden="true" />
                                        </Link>
                                    </Button>
                                </div>
                            </div>

                            {asset.hasDataRoom && (
                                <AssetDealAssistantCard asset={asset} />
                            )}

                            {asset.hasDataRoom && (
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        DeFi Integrations
                                    </p>
                                    <div className="mt-3 overflow-x-auto rounded-md border border-muted">
                                        <table className="min-w-full divide-y divide-muted text-left text-xs">
                                            <thead className="text-[11px] uppercase tracking-wide text-tertiary-foreground">
                                                <tr>
                                                    <th className="px-3 py-2 font-medium">
                                                        Name
                                                    </th>
                                                    <th className="px-3 py-2 font-medium">
                                                        Network
                                                    </th>
                                                    <th className="px-3 py-2 font-medium">
                                                        Type
                                                    </th>
                                                    <th className="px-3 py-2 text-right font-medium">
                                                        TVL
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-muted text-secondary-foreground">
                                                {DEFI_INTEGRATION_PLACEHOLDER_ROWS.map(
                                                    (integration) => (
                                                        <tr
                                                            key={
                                                                integration.name
                                                            }
                                                        >
                                                            <td className="px-3 py-2">
                                                                <div className="flex items-center gap-2.5">
                                                                    <span
                                                                        className={cn(
                                                                            "flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
                                                                            integration.iconClassName,
                                                                        )}
                                                                    >
                                                                        {
                                                                            integration.icon
                                                                        }
                                                                    </span>
                                                                    <span>
                                                                        {
                                                                            integration.name
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <div className="flex items-center gap-2.5">
                                                                    <span
                                                                        className={cn(
                                                                            "flex size-4 shrink-0 items-center justify-center rounded text-[9px] font-semibold",
                                                                            integration.networkClassName,
                                                                        )}
                                                                    >
                                                                        {
                                                                            integration
                                                                                .network[0]
                                                                        }
                                                                    </span>
                                                                    <span>
                                                                        {
                                                                            integration.network
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                {
                                                                    integration.type
                                                                }
                                                            </td>
                                                            <td className="px-3 py-2 text-right tabular-nums">
                                                                {
                                                                    integration.tvl
                                                                }
                                                            </td>
                                                        </tr>
                                                    ),
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div> */}
                </div>
            </div>
        </AnimatedExpandedDetails>
    );
}
